
<template>
  <div class="main-slider">
    <div class="main-slider js-slider">
      <div class="main-slider__inner js-slider__inner"></div>
      <Slide v-for="(photoGroup, index) of photos" :key="index" :photos="photoGroup"></Slide>
      <nav class="main-slider__nav js-slider__nav">
        <Bullet v-for="index in photos.length" :index="index" :key="index"></Bullet>
      </nav>
    </div>
  </div>
</template>

<script>
import { TimelineMax, TweenMax, Expo, Linear, Power4 } from "gsap";
import * as THREE from "three";

import Slide from "./MainSliderPhoto";
import Bullet from "./MainSliderBullet";

export default {
  props: {
    isResourcesLoaded: {
      type: Boolean,
      default: false
    },
    verticalPhotos: [],
    horizontalPhotos: []
  },
  methods: {
    initSlider() {
      this.bindAll();
      this.setup();
      this.cameraSetup();
      this.loadTextures();
      this.createMesh();
      this.setStyles();
      this.render();
      this.runTimer();
    },
    bindAll() {
      ["render", "nextSlide"].forEach(fn => (this[fn] = this[fn].bind(this)));
    },
    setStyles() {
      this.slides.forEach(
        (slide, index) => index > 0 && TweenMax.set(slide, { autoAlpha: 0 })
      );
      this.bullets.forEach((bullet, index) => {
        if (index === 0) return;

        const txt = bullet.querySelector(".js-slider-bullet__text");
        const line = bullet.querySelector(".js-slider-bullet__line");

        TweenMax.set(txt, { alpha: 0.25 });
        TweenMax.set(line, { scaleX: 0, transformOrigin: "left" });
      });
    },
    cameraSetup() {
      this.camera = new THREE.OrthographicCamera(
        this.el.offsetWidth / -2,
        this.el.offsetWidth / 2,
        this.el.offsetHeight / 2,
        this.el.offsetHeight / -2,
        1,
        1000
      );

      this.camera.lookAt(this.scene.position);
      this.camera.position.z = 1;
    },
    setup() {
      this.scene = new THREE.Scene();
      this.clock = new THREE.Clock(true);

      this.renderer = new THREE.WebGLRenderer({ alpha: true });
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(this.el.offsetWidth, this.el.offsetHeight);

      this.inner.appendChild(this.renderer.domElement);
    },
    loadTextures() {
      const loader = new THREE.TextureLoader();
      loader.crossOrigin = "";

      this.textures = [];
      this.images.forEach((image, index) => {
        const texture = loader.load(image + "?v=" + Date.now(), this.render);

        texture.minFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;

        if (index === 0 && this.mat) {
          this.mat.uniforms.size.value = [
            texture.image.naturalWidth,
            texture.image.naturalHeight
          ];
        }

        this.textures.push(texture);
      });

      this.disp = loader.load(
        "https://s3-us-west-2.amazonaws.com/s.cdpn.io/58281/rock-_disp.png",
        this.render
      );
      this.disp.magFilter = this.disp.minFilter = THREE.LinearFilter;
      this.disp.wrapS = this.disp.wrapT = THREE.RepeatWrapping;
    },
    createMesh() {
      this.mat = new THREE.ShaderMaterial({
        uniforms: {
          dispPower: { type: "f", value: 0.0 },
          intensity: { type: "f", value: 0.5 },
          res: {
            value: new THREE.Vector2(window.innerWidth, window.innerHeight)
          },
          size: { value: new THREE.Vector2(1, 1) },
          texture1: { type: "t", value: this.textures[0] },
          texture2: { type: "t", value: this.textures[1] },
          disp: { type: "t", value: this.disp }
        },
        transparent: true,
        vertexShader: this.vert,
        fragmentShader: this.frag
      });

      const geometry = new THREE.PlaneBufferGeometry(
        this.el.offsetWidth,
        this.el.offsetHeight,
        1
      );

      const mesh = new THREE.Mesh(geometry, this.mat);

      this.scene.add(mesh);
    },
    transitionNext() {
      TweenMax.to(this.mat.uniforms.dispPower, 2.5, {
        value: 1,
        ease: Expo.easeInOut,
        onUpdate: this.render,
        onComplete: () => {
          this.mat.uniforms.dispPower.value = 0.0;
          this.changeTexture();
          this.render.bind(this);
          this.state.animating = false;
        }
      });

      const current = this.slides[this.data.current];
      const next = this.slides[this.data.next];

      const currentImages = current.querySelectorAll(".js-slide__img");
      const nextImages = next.querySelectorAll(".js-slide__img");

      const currentBullet = this.bullets[this.data.current];
      const nextBullet = this.bullets[this.data.next];

      const currentBulletTxt = currentBullet.querySelectorAll(
        ".js-slider-bullet__text"
      );
      const nextBulletTxt = nextBullet.querySelectorAll(
        ".js-slider-bullet__text"
      );

      const currentBulletLine = currentBullet.querySelectorAll(
        ".js-slider-bullet__line"
      );
      const nextBulletLine = nextBullet.querySelectorAll(
        ".js-slider-bullet__line"
      );

      const tl = new TimelineMax({ paused: true });

      if (this.state.initial) {
        TweenMax.to(".js-scroll", 1.5, {
          yPercent: 100,
          alpha: 0,
          ease: Power4.easeInOut
        });

        this.state.initial = false;
      }

      tl.staggerFromTo(
        currentImages,
        1.5,
        {
          yPercent: 0,
          scale: 1
        },
        {
          yPercent: -185,
          scaleY: 1.5,
          ease: Expo.easeInOut
        },
        0.075
      )
        .to(
          currentBulletTxt,
          1.5,
          {
            alpha: 0.25,
            ease: Linear.easeNone
          },
          0
        )
        .set(
          currentBulletLine,
          {
            transformOrigin: "right"
          },
          0
        )
        .to(
          currentBulletLine,
          1.5,
          {
            scaleX: 0,
            ease: Expo.easeInOut
          },
          0
        );

      tl.set(current, {
        autoAlpha: 0
      }).set(
        next,
        {
          autoAlpha: 1
        },
        1
      );

      tl.staggerFromTo(
        nextImages,
        1.5,
        {
          yPercent: 150,
          scaleY: 1.5
        },
        {
          yPercent: 0,
          scaleY: 1,
          ease: Expo.easeInOut
        },
        0.075,
        1
      )
        .to(
          nextBulletTxt,
          1.5,
          {
            alpha: 1,
            ease: Linear.easeNone
          },
          1
        )
        .set(
          nextBulletLine,
          {
            transformOrigin: "left"
          },
          1
        )
        .to(
          nextBulletLine,
          1.5,
          {
            scaleX: 1,
            ease: Expo.easeInOut
          },
          1
        );

      tl.play();
    },
    prevSlide() {},
    nextSlide() {
      if (this.state.animating) return;
      
      this.state.animating = true;

      this.transitionNext();

      const total = this.data.total / 2;

      this.data.current =
        this.data.current + 1 === total ? 0 : this.data.current + 1;

      this.data.next =
        this.data.next + 1 === total ? 0 : this.data.next + 1;
    },
    changeTexture() {
      this.mat.uniforms.texture1.value = this.textures[this.data.current];
      this.mat.uniforms.texture2.value = this.textures[this.data.next];
    },
    listeners() {
      window.addEventListener("wheel", this.nextSlide, { passive: true });
    },
    runTimer() {
      const delay = 4000;
      this.intervalID = setInterval(() => this.nextSlide(), delay);
    },
    render() {
      this.renderer.render(this.scene, this.camera);
    }
  },
  destroyed() {
    clearInterval(this.intervalID);
  },
  computed: {
    vert() {
      return `varying vec2 vUv;
              void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }`;
    },
    frag() {
      return `varying vec2 vUv;
          uniform sampler2D texture1;
          uniform sampler2D texture2;
          uniform sampler2D disp;

          uniform float dispPower;
          uniform float intensity;

          uniform vec2 size;
          uniform vec2 res;

          vec2 backgroundCoverUv( vec2 screenSize, vec2 imageSize, vec2 uv ) {
            float screenRatio = screenSize.x / screenSize.y;
            float imageRatio = imageSize.x / imageSize.y;
            vec2 newSize = screenRatio < imageRatio 
                ? vec2(imageSize.x * (screenSize.y / imageSize.y), screenSize.y)
                : vec2(screenSize.x, imageSize.y * (screenSize.x / imageSize.x));
            vec2 newOffset = (screenRatio < imageRatio 
                ? vec2((newSize.x - screenSize.x) / 2.0, 0.0) 
                : vec2(0.0, (newSize.y - screenSize.y) / 2.0)) / newSize;
            return uv * screenSize / newSize + newOffset;
          }

          void main() {
            vec2 uv = vUv;
            
            vec4 disp = texture2D(disp, uv);
            vec2 dispVec = vec2(disp.x, disp.y);
            
            vec2 distPos1 = uv + (dispVec * intensity * dispPower);
            vec2 distPos2 = uv + (dispVec * -(intensity * (1.0 - dispPower)));
            
            vec4 _texture1 = texture2D(texture1, distPos1);
            vec4 _texture2 = texture2D(texture2, distPos2);
            
            gl_FragColor = mix(_texture1, _texture2, dispPower);
          }`;
    },
    photos() {
      const groupedPhotos = [];
      let currentIndex = 0;

      for (
        let i = 0;
        i < this.verticalPhotos.length / 2;
        i++, currentIndex += 2
      ) {
        groupedPhotos.push([
          this.verticalPhotos[currentIndex],
          this.verticalPhotos[currentIndex + 1]
        ]);
      }

      return groupedPhotos;
    }
  },
  mounted() {
    const self = this;
    const intervalPeriod = 50;

    const timerId = setInterval(() => {
      if (!self.isResourcesLoaded) {
        return;
      }

      self.el = document.querySelector(".js-slider");
      self.inner = self.el.querySelector(".js-slider__inner");
      self.slides = [...self.el.querySelectorAll(".js-slide")];
      self.bullets = [...self.el.querySelectorAll(".js-slider-bullet")];
      self.renderer = null;
      self.scene = null;
      self.clock = null;
      self.camera = null;
      self.images = self.horizontalPhotos.map(photo => photo.url);
      self.data = {
        current: 0,
        next: 1,
        total: self.images.length,
        delta: 0
      };
      self.state = {
        animating: false,
        text: false,
        initial: true
      };
      self.textures = null;

      self.initSlider();
      clearInterval(timerId);
    }, intervalPeriod);
  },
  components: {
    Slide,
    Bullet
  }
};
</script>

<style lang="scss">
.main-slider {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;

  &__text {
    position: absolute;
    bottom: calc(2rem + 3vw);
    left: calc(2rem + 3vw);
    z-index: 10;
    font-size: calc(1rem + 4vw);
    text-transform: uppercase;
    transform-origin: top;
    line-height: 1.075;
    color: #fff;
    font-weight: 500;

    &-line {
      overflow: hidden;
    }
  }

  &__inner {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1;

    &:before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
      background-color: rgba(#000, 0.5);
    }
  }

  &__nav {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    z-index: 10;
    display: flex;
    flex-direction: column;
  }

  &-bullet {
    display: flex;
    align-items: center;
    padding: 1rem 0;

    &__text {
      color: #fff;
      font-size: 1.8rem;
      margin-right: 1rem;
      font-weight: 300;
    }

    &__line {
      background-color: #fff;
      height: 0.2rem;
      width: 2rem;
    }
  }

  canvas {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
}

.main-slide {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 2;

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }

  &__img {
    position: relative;
    width: 30vw;
    height: 70vh;
    padding: 0;
    margin: 0;
    min-width: 12.5rem;
    transform-origin: top;

    &:first-child {
      top: -1.5rem;
      margin-right: 2rem;
    }

    &:last-child {
      bottom: -1.5rem;
      margin-left: 2rem;
    }

    img {
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
</style>